// PartnerHealthPage — FREEEMAN診断 (2026-04-24)
// 過去12ヶ月のfreee deal を元に、取引先cadence と金額統計で
// 抜け/過払い を AI 診断。3グループ (A仕入/B仕入外/C当月発生当月払い) に分類。
//
// UI/UX: モダン・シンプル・革新的
//   - 月selector + 分析開始 button
//   - TabBar で3グループ切替
//   - 各行にヒートマップ12セル+sparkline+診断StatusIcon
//   - 右 SidePanel で過去12ヶ月 deals + anomaly ハイライト

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  PageTitle,
  CardBase,
  HStack,
  VStack,
  Button,
  SelectBox,
  SearchField,
  Text,
  Note,
  Breadcrumbs,
  MessageBlock,
  TabBar,
  StatusIcon,
  SegmentControlButton,
  NoDataIllust,
  InlineSpinner,
} from '@freee_jp/vibes';
import {
  runPartnerHealthAnalysis,
  fetchPartnerHealthSnapshot,
  fetchPartnerHealthDetail,
  PartnerHealthSnapshot,
  HealthGroup,
  AnomalyGrade,
  HealthAnalysisRun,
} from '../api/client';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const S = {
  // 2026-04-24 モダン刷新: subtle gradient bg + glass card + micro-animation
  page: {
    background: 'linear-gradient(180deg, #f8f7f5 0%, #f3f1ef 100%)',
    minHeight: '100vh', paddingBottom: '3rem',
  } as React.CSSProperties,
  mainLayout: { display: 'grid', gridTemplateColumns: '1fr', gap: 0 } as React.CSSProperties,
  mainLayoutWithPanel: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 460px',
    gap: '16px',
    alignItems: 'stretch',
  } as React.CSSProperties,
  thCell: {
    padding: '11px 14px', fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560',
    textTransform: 'uppercase' as const, letterSpacing: '0.06em',
    whiteSpace: 'nowrap' as const, borderBottom: '1px solid #e5e4e2',
    background: 'linear-gradient(180deg, #fafaf8 0%, #f5f3f2 100%)',
    userSelect: 'none' as const,
  } as React.CSSProperties,
  tdCell: {
    padding: '12px 14px', fontSize: '0.8125rem',
    verticalAlign: 'middle' as const, borderBottom: '1px solid #f0efed',
  } as React.CSSProperties,
  // モダンKPIカード (glassmorphism 風)
  kpiCard: (accent: string): React.CSSProperties => ({
    background: `linear-gradient(135deg, ${accent}12 0%, ${accent}05 100%)`,
    border: `1px solid ${accent}22`,
    borderRadius: '12px',
    padding: '16px 20px',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'default',
  }),
  kpiAccent: (color: string): React.CSSProperties => ({
    position: 'absolute',
    top: 0, left: 0,
    width: '3px', height: '100%',
    background: color,
  }),
  row: (active: boolean) => ({
    cursor: 'pointer',
    transition: 'background 0.12s ease',
    background: active ? '#ebf3ff' : 'transparent',
    borderLeft: active ? '3px solid #2e7cf6' : '3px solid transparent',
  }),
  sidePanel: {
    position: 'sticky' as const, top: '16px', maxHeight: 'calc(100vh - 32px)',
    display: 'flex', flexDirection: 'column' as const, overflow: 'hidden',
    background: '#fff', borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e4e2',
  } as React.CSSProperties,
};

const GROUP_META: Record<HealthGroup, { label: string; detail: string; accrual: string }> = {
  A: { label: '買掛金 (仕入高)', detail: '仕入勘定', accrual: '858355765' },
  B: { label: '未払金 (仕入外)', detail: '仕入以外の未払', accrual: '858355768' },
  C: { label: '当月発生当月払い', detail: '家賃・顧問料等', accrual: '858355769' },
  excluded: { label: '対象外', detail: 'partner_id なし', accrual: '—' },
};

const GRADE_TO_ICON: Record<AnomalyGrade, 'done' | 'warning' | 'error' | 'required' | 'disabled' | 'progress'> = {
  green: 'done',
  yellow: 'warning',
  red: 'error',
  stale: 'progress',
  expired: 'disabled',
  new: 'required',
  excluded: 'disabled',
};

// 分析語 (ワークフロー状態ではない) — 統一バッジ4色の対象外。「要確認」は予約語のため「注意」に改名 (2026-07-08)
const GRADE_LABEL: Record<AnomalyGrade, string> = {
  green: '正常',
  yellow: '注意',
  red: '異常',
  stale: '要注意',
  expired: '停止候補',
  new: '新規',
  excluded: '対象外',
};

// ---------------------------------------------------------------------------
// Month helpers
// ---------------------------------------------------------------------------
const currentYyyyMm = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthOffset = (yyyymm: string, offset: number) => {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(y, m - 1 + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const fmt = (n: number | null | undefined) => n == null ? '—' : `¥${n.toLocaleString('ja-JP')}`;

// ---------------------------------------------------------------------------
// Heatmap: 12セル monthly amount を色濃度で表示
// ---------------------------------------------------------------------------
const Heatmap: React.FC<{
  monthlyHistory: Array<{ month: string; amount: number; deal_count: number }>;
  targetMonth: string;
}> = ({ monthlyHistory, targetMonth }) => {
  const last12 = useMemo(() => {
    const arr: Array<{ month: string; amount: number; deal_count: number }> = [];
    for (let i = 11; i >= 0; i--) {
      const m = monthOffset(targetMonth, -i);
      const found = monthlyHistory.find((h) => h.month === m);
      arr.push(found || { month: m, amount: 0, deal_count: 0 });
    }
    return arr;
  }, [monthlyHistory, targetMonth]);
  const maxAmount = Math.max(...last12.map((m) => m.amount), 1);
  return (
    <div style={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }}>
      {last12.map((m) => {
        const ratio = m.amount === 0 ? 0 : 0.25 + 0.75 * Math.sqrt(m.amount / maxAmount);
        const isTarget = m.month === targetMonth;
        const bg = m.amount === 0
          ? '#f0efed'
          : `linear-gradient(135deg, rgba(51,134,80,${Math.min(ratio + 0.15, 1)}) 0%, rgba(76,175,80,${ratio}) 100%)`;
        return (
          <div
            key={m.month}
            className="phx-heat-cell"
            style={{
              width: '13px', height: '22px',
              background: bg,
              border: isTarget ? '2px solid #2e7cf6' : '1px solid rgba(0,0,0,0.06)',
              boxShadow: isTarget ? '0 0 0 2px rgba(46,124,246,0.15)' : 'none',
              boxSizing: 'border-box',
            }}
            title={`${m.month}: ${fmt(m.amount)} (${m.deal_count}件)`}
          />
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sparkline: 12ヶ月金額推移
// ---------------------------------------------------------------------------
const Sparkline: React.FC<{
  monthlyHistory: Array<{ month: string; amount: number; deal_count: number }>;
  targetMonth: string;
}> = ({ monthlyHistory, targetMonth }) => {
  const W = 100, H = 20;
  const last12 = useMemo(() => {
    const arr: Array<{ month: string; amount: number }> = [];
    for (let i = 11; i >= 0; i--) {
      const m = monthOffset(targetMonth, -i);
      const found = monthlyHistory.find((h) => h.month === m);
      arr.push(found || { month: m, amount: 0 });
    }
    return arr;
  }, [monthlyHistory, targetMonth]);
  const maxAmount = Math.max(...last12.map((m) => m.amount), 1);
  const points = last12.map((m, i) => {
    const x = (i / 11) * W;
    const y = H - (m.amount / maxAmount) * H * 0.9;
    return `${x},${y}`;
  }).join(' ');
  const currentIdx = last12.findIndex((m) => m.month === targetMonth);
  const currentPt = last12[currentIdx];
  return (
    <svg width={W} height={H} style={{ display: 'inline-block' }}>
      <polyline points={points} fill="none" stroke="#2e7cf6" strokeWidth="1.5" />
      {currentIdx >= 0 && currentPt && (
        <circle
          cx={(currentIdx / 11) * W}
          cy={H - (currentPt.amount / maxAmount) * H * 0.9}
          r="3" fill="#c33939"
        />
      )}
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export const PartnerHealthPage: React.FC = () => {
  const [targetMonth, setTargetMonth] = useState<string>(() => monthOffset(currentYyyyMm(), -1));
  const [group, setGroup] = useState<HealthGroup>('A');
  const [gradeFilter, setGradeFilter] = useState<AnomalyGrade | 'all'>('all');
  const [searchText, setSearchText] = useState('');
  const [items, setItems] = useState<PartnerHealthSnapshot[]>([]);
  const [summary, setSummary] = useState<Array<{ group_type: HealthGroup; anomaly_grade: AnomalyGrade; n: number; total: number }>>([]);
  const [latestRun, setLatestRun] = useState<HealthAnalysisRun | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activePartnerId, setActivePartnerId] = useState<number | null>(null);
  const [detail, setDetail] = useState<any>(null);

  // Month options (当月 -12ヶ月 〜 +1ヶ月)
  const monthOptions = useMemo(() => {
    const now = currentYyyyMm();
    const arr: Array<{ name: string; value: string }> = [];
    for (let i = -12; i <= 1; i++) arr.push({ name: monthOffset(now, i), value: monthOffset(now, i) });
    return arr;
  }, []);

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchPartnerHealthSnapshot({ target_month: targetMonth });
      setItems(r.items);
      setSummary(r.summary);
      setLatestRun(r.latest_run);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [targetMonth]);

  useEffect(() => { loadSnapshot(); }, [loadSnapshot]);

  const onAnalyze = useCallback(async () => {
    setAnalyzing(true);
    setError(null);
    try {
      const r = await runPartnerHealthAnalysis(targetMonth);
      await loadSnapshot();
      alert(`分析完了: ${r.partners_analyzed}社 / ${r.deals_analyzed}件 / 異常 ${r.anomalies_red}, 注意 ${r.anomalies_yellow}, 抜け ${r.missing_count} (${Math.round(r.duration_ms / 1000)}秒)`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAnalyzing(false);
    }
  }, [targetMonth, loadSnapshot]);

  // Filter items by group + grade + search
  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (it.group_type !== group) return false;
      if (gradeFilter !== 'all' && it.anomaly_grade !== gradeFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!(it.partner_name || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [items, group, gradeFilter, searchText]);

  // Group counts
  const groupCounts = useMemo(() => {
    const c: Record<HealthGroup, number> = { A: 0, B: 0, C: 0, excluded: 0 };
    for (const s of summary) c[s.group_type] = (c[s.group_type] || 0) + s.n;
    return c;
  }, [summary]);

  // Grade counts (current group)
  const gradeCounts = useMemo(() => {
    const c: Record<AnomalyGrade, number> = {
      green: 0, yellow: 0, red: 0, stale: 0, expired: 0, new: 0, excluded: 0,
    };
    for (const s of summary) {
      if (s.group_type === group) c[s.anomaly_grade] = (c[s.anomaly_grade] || 0) + s.n;
    }
    return c;
  }, [summary, group]);

  const onRowClick = useCallback(async (pid: number | null) => {
    if (!pid) return;
    setActivePartnerId(pid);
    try {
      const d = await fetchPartnerHealthDetail(pid, targetMonth);
      setDetail(d);
    } catch (e) {
      setDetail(null);
    }
  }, [targetMonth]);

  const renderTable = () => (
    <CardBase paddingSize="zero" overflowHidden={false}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '200px' }} />
            <col style={{ width: '60px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '180px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '120px' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={S.thCell}>取引先</th>
              <th style={{ ...S.thCell, textAlign: 'right' }}>12M件</th>
              <th style={{ ...S.thCell, textAlign: 'right' }}>12M合計</th>
              <th style={S.thCell}>ヒートマップ</th>
              <th style={{ ...S.thCell, textAlign: 'right' }}>当月</th>
              <th style={S.thCell}>推移</th>
              <th style={{ ...S.thCell, textAlign: 'center' }}>診断</th>
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center' }}><InlineSpinner isLoading />読込中…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center' }}>
                  <VStack gap={0.5} alignItems="center">
                    <NoDataIllust />
                    <Text size={0.875} color="burnt">該当する取引先がありません</Text>
                    <Note>{latestRun ? `(${latestRun.completed_at} に分析実行済)` : '先に「分析開始」を押してください'}</Note>
                  </VStack>
                </td>
              </tr>
            )}
            {filtered.map((it) => {
              const history = (() => {
                try { return JSON.parse(it.monthly_history_json || '[]'); } catch { return []; }
              })();
              const reasons = (() => {
                try { return JSON.parse(it.anomaly_reasons || '[]'); } catch { return []; }
              })();
              const isActive = activePartnerId === it.partner_id;
              return (
                <tr
                  key={it.id}
                  className="phx-row"
                  style={S.row(isActive)}
                  onClick={() => onRowClick(it.partner_id)}
                >
                  <td style={S.tdCell}>
                    <div className="phx-partner-name" style={{ fontWeight: 600, color: '#252525', fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'color 0.15s' }}>
                      {it.partner_name || `partner:${it.partner_id}`}
                    </div>
                    <div style={{ fontSize: '0.625rem', color: '#8a8580', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ padding: '1px 6px', background: '#f0efed', borderRadius: '3px', fontSize: '0.625rem' }}>
                        {it.cadence_pattern || '—'}
                      </span>
                      {it.cadence_confidence != null && (
                        <span>conf {(it.cadence_confidence * 100).toFixed(0)}%</span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...S.tdCell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {it.past_12m_deal_count}
                  </td>
                  <td style={{ ...S.tdCell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {fmt(it.past_12m_total)}
                  </td>
                  <td style={S.tdCell}>
                    <Heatmap monthlyHistory={history} targetMonth={it.target_month} />
                  </td>
                  <td style={{ ...S.tdCell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {it.actual_count && it.actual_count > 0
                      ? <><div>{fmt(it.actual_total)}</div><div style={{ fontSize: '0.625rem', color: '#8a8580' }}>{it.actual_count}件</div></>
                      : <span style={{ color: '#c33939', fontSize: '0.75rem' }}>未発生</span>}
                  </td>
                  <td style={S.tdCell}>
                    <Sparkline monthlyHistory={history} targetMonth={it.target_month} />
                  </td>
                  <td style={{ ...S.tdCell, textAlign: 'center' }}>
                    <div title={reasons.join(' / ')}>
                      <StatusIcon type={GRADE_TO_ICON[it.anomaly_grade]}>{GRADE_LABEL[it.anomaly_grade]}</StatusIcon>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CardBase>
  );

  const renderSidePanel = () => {
    if (!activePartnerId || !detail) return null;
    const snap: PartnerHealthSnapshot = detail.snapshot;
    const deals: any[] = detail.deals || [];
    const reasons: string[] = (() => { try { return JSON.parse(snap.anomaly_reasons || '[]'); } catch { return []; } })();
    const history = (() => { try { return JSON.parse(snap.monthly_history_json || '[]'); } catch { return []; } })();
    return (
      <div style={S.sidePanel}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #edebea' }}>
          <HStack gap={0.5} alignItems="center">
            <StatusIcon type={GRADE_TO_ICON[snap.anomaly_grade]}>{GRADE_LABEL[snap.anomaly_grade]}</StatusIcon>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#252525' }}>{snap.partner_name}</div>
              <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>
                {GROUP_META[snap.group_type].label} · {snap.cadence_pattern || '—'}
              </div>
            </div>
            <Button appearance="tertiary" small onClick={() => setActivePartnerId(null)}>閉じる</Button>
          </HStack>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          <VStack gap={1}>
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                <div style={{ padding: '10px', background: '#f7f5f5', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>median</div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(snap.median_amount)}</div>
                </div>
                <div style={{ padding: '10px', background: '#f7f5f5', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>12M合計</div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(snap.past_12m_total)}</div>
                </div>
                <div style={{ padding: '10px', background: '#f7f5f5', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>当月</div>
                  <div style={{ fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {snap.actual_count && snap.actual_count > 0 ? fmt(snap.actual_total) : <span style={{ color: '#c33939' }}>未発生</span>}
                  </div>
                </div>
              </div>
            </div>

            {reasons.length > 0 && (
              <MessageBlock notice>
                <VStack gap={0.25}>
                  {reasons.map((r, i) => <Text key={i} size={0.75}>• {r}</Text>)}
                </VStack>
              </MessageBlock>
            )}

            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b6560', marginBottom: '6px' }}>12ヶ月推移</div>
              <Sparkline monthlyHistory={history} targetMonth={snap.target_month} />
            </div>

            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b6560', marginBottom: '6px' }}>
                過去 deals ({deals.length}件)
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #edebea', borderRadius: '4px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '6px 8px', textAlign: 'left', background: '#fafaf8', position: 'sticky', top: 0 }}>発生日</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', background: '#fafaf8', position: 'sticky', top: 0 }}>金額</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', background: '#fafaf8', position: 'sticky', top: 0 }}>科目</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map((d) => (
                      <tr key={d.deal_id}>
                        <td style={{ padding: '4px 8px', fontVariantNumeric: 'tabular-nums' }}>{d.issue_date}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(d.total_amount)}</td>
                        <td style={{ padding: '4px 8px', color: '#6b6560' }}>{d.first_account_item_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b6560', marginBottom: '6px' }}>予測</div>
              <VStack gap={0.25}>
                <HStack gap={0.5} alignItems="center"><Note>発生予想</Note><Text size={0.75}>{snap.expected_issue_date || '—'}</Text></HStack>
                <HStack gap={0.5} alignItems="center"><Note>期日予想</Note><Text size={0.75}>{snap.expected_due_date || '—'}</Text></HStack>
                <HStack gap={0.5} alignItems="center"><Note>金額予想</Note><Text size={0.75}>{fmt(snap.expected_amount)}</Text></HStack>
              </VStack>
            </div>
          </VStack>
        </div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <style>{`
        @keyframes phx-fadein { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes phx-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes phx-pulse { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
        .phx-kpi { animation: phx-fadein 0.3s ease-out; }
        .phx-kpi:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.06); }
        .phx-row { transition: background 0.12s ease, transform 0.12s ease; }
        .phx-row:hover { background: linear-gradient(90deg, #f0f6ff 0%, #f7faff 100%); }
        .phx-row:hover .phx-partner-name { color: #2e7cf6; }
        .phx-grade-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; }
        .phx-heat-cell { transition: transform 0.15s ease; border-radius: 3px; }
        .phx-heat-cell:hover { transform: scale(1.25); z-index: 10; position: relative; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .phx-grad-bar { background: linear-gradient(90deg, #e8ecef 0%, #e8ecef 100%); border-radius: 2px; overflow: hidden; }
        .phx-loading { background: linear-gradient(90deg, #f0efed 25%, #e5e4e2 50%, #f0efed 75%); background-size: 200% 100%; animation: phx-shimmer 1.5s infinite; }
        .phx-pulse { animation: phx-pulse 1.6s ease-in-out infinite; }
      `}</style>
      <Container width="wide">
        {/* Breadcrumbs + Hero Header */}
        <div style={{ padding: '20px 0 10px' }}>
          <Breadcrumbs links={[{ title: 'FREEEMAN', url: '/' }, { title: 'FREEEMAN診断' }, { title: targetMonth }]} />
        </div>

        {/* Hero: Title + Gradient Icon + Action Bar */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          flexWrap: 'wrap', gap: '16px', marginBottom: '18px',
        }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              padding: '4px 12px', borderRadius: '20px',
              background: 'linear-gradient(135deg, #2e7cf6 0%, #5b9bf9 100%)',
              color: '#fff', fontSize: '0.6875rem', fontWeight: 600,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              marginBottom: '6px',
              boxShadow: '0 2px 8px rgba(46,124,246,0.25)',
            }}>
              AI DIAGNOSIS · 過去12ヶ月分析
            </div>
            <PageTitle>FREEEMAN診断</PageTitle>
            <div style={{ marginTop: '4px', fontSize: '0.8125rem', color: '#6b6560' }}>
              全取引先の cadence × 金額統計で抜け・過払いを検出
            </div>
          </div>
          <HStack gap={0.5}>
            <SelectBox
              options={monthOptions}
              value={targetMonth}
              onChange={(e) => setTargetMonth(e.target.value)}
              width="small" small label="対象月"
            />
            <Button appearance="primary" onClick={onAnalyze} disabled={analyzing}>
              {analyzing ? '分析中…' : '▸ 分析開始'}
            </Button>
            <Button appearance="secondary" small onClick={loadSnapshot} disabled={loading}>
              {loading ? '...' : '再読込'}
            </Button>
          </HStack>
        </div>

        {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

        {/* Hero KPI 4 Cards (glassmorphism) */}
        {latestRun && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px', marginBottom: '16px',
          }}>
            <div className="phx-kpi" style={S.kpiCard('#2e7cf6')}>
              <div style={S.kpiAccent('#2e7cf6')} />
              <div style={{ fontSize: '0.6875rem', color: '#6b6560', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>分析対象</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#252525', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
                {latestRun.partners_analyzed}<span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#8a8580', marginLeft: '4px' }}>社</span>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#8a8580', marginTop: '4px' }}>
                {latestRun.deals_analyzed.toLocaleString()} deal 集計
              </div>
            </div>
            <div className="phx-kpi" style={S.kpiCard('#c33939')}>
              <div style={S.kpiAccent('#c33939')} />
              <div style={{ fontSize: '0.6875rem', color: '#6b6560', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>異常 (Red)</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#c33939', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
                {latestRun.anomalies_red}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#8a8580', marginTop: '4px' }}>
                robust Z {'>'} 3.5 or missing
              </div>
            </div>
            <div className="phx-kpi" style={S.kpiCard('#dd7a00')}>
              <div style={S.kpiAccent('#dd7a00')} />
              <div style={{ fontSize: '0.6875rem', color: '#6b6560', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>注意 (Yellow)</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#dd7a00', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
                {latestRun.anomalies_yellow}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#8a8580', marginTop: '4px' }}>
                robust Z 2.5–3.5
              </div>
            </div>
            <div className="phx-kpi" style={S.kpiCard('#8a5cf6')}>
              <div style={S.kpiAccent('#8a5cf6')} />
              <div style={{ fontSize: '0.6875rem', color: '#6b6560', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>抜け検出</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#8a5cf6', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
                {latestRun.missing_count}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#8a8580', marginTop: '4px' }}>
                最終分析 {latestRun.completed_at?.slice(5, 16)}
              </div>
            </div>
          </div>
        )}

        {/* Group Tabs: modern pill design with gradient on active */}
        <div style={{
          background: '#fff', borderRadius: '12px',
          border: '1px solid #e5e4e2',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          marginBottom: '12px',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', gap: 0, padding: '6px', background: '#f7f5f5',
            borderBottom: '1px solid #e5e4e2',
          }}>
            {(['A', 'B', 'C'] as const).map((g) => {
              const isActive = group === g;
              const label = g === 'A' ? '買掛金 (仕入高)' : g === 'B' ? '未払金 (仕入外)' : '当月発生当月払い';
              const icon = g === 'A' ? '📦' : g === 'B' ? '📋' : '🏠';
              return (
                <button
                  key={g}
                  onClick={() => setGroup(g)}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#fff' : '#6b6560',
                    background: isActive
                      ? 'linear-gradient(135deg, #2e7cf6 0%, #5b9bf9 100%)'
                      : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isActive ? '0 2px 8px rgba(46,124,246,0.3)' : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ fontSize: '1rem' }}>{icon}</span>
                  <span>
                    <span style={{ opacity: 0.6, marginRight: '6px', fontWeight: 500 }}>{g}</span>
                    {label}
                  </span>
                  <span style={{
                    fontSize: '0.6875rem',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    background: isActive ? 'rgba(255,255,255,0.25)' : '#e5e4e2',
                    color: isActive ? '#fff' : '#6b6560',
                    fontWeight: 700,
                  }}>
                    {groupCounts[g]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Filter bar */}
          <div style={{
            padding: '10px 14px', display: 'flex', flexWrap: 'wrap',
            gap: '10px', alignItems: 'center',
          }}>
            <SearchField
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="取引先を検索 (/)"
              width="medium" small
            />
            <div style={{ display: 'inline-flex' }}>
              {(['all', 'red', 'yellow', 'green', 'new', 'stale', 'expired'] as const).map((g) => {
                const cnt = g === 'all' ? Object.values(gradeCounts).reduce((s, v) => s + v, 0) : gradeCounts[g];
                return (
                  <SegmentControlButton
                    key={g}
                    current={gradeFilter === g}
                    small
                    onClick={() => setGradeFilter(g)}
                  >
                    {g === 'all' ? '全て' : GRADE_LABEL[g as AnomalyGrade]}
                    <span style={{ marginLeft: '6px', opacity: 0.7 }}>{cnt}</span>
                  </SegmentControlButton>
                );
              })}
            </div>
            <div style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#8a8580' }}>
              <span style={{ fontWeight: 600, color: '#252525' }}>{filtered.length}</span>
              <span style={{ margin: '0 4px', color: '#c4c0bc' }}>/</span>
              <span>{groupCounts[group]}件</span>
            </div>
          </div>
        </div>

        <div style={activePartnerId ? S.mainLayoutWithPanel : S.mainLayout}>
          {renderTable()}
          {activePartnerId && renderSidePanel()}
        </div>
      </Container>
    </div>
  );
};
